import {
  EuiButton,
  EuiButtonEmpty,
  EuiCopy,
  EuiFieldText,
  EuiForm,
  EuiFormRow,
  EuiIcon,
  EuiLink,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { useMemo, useState } from "react";
import { AnketaResourcesEnum } from "src/auth";
import { useApi } from "src/api";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";
import { useAnketaAuth } from "../../../auth/context";

export interface OnboardProjectModalProps {
  catalogId: string;
  questionnaireId: string;
  /** Free-form label for the VPC (common.metadata.name) shown in the dialog. */
  vpcName?: string;
  onClose: () => void;
}

/** Operator runbook for manually creating the infrastructure project. */
const DOCUMENTATION_VPC_GUIDE_URL =
  "https://gitlab.autonubil.net/infrastructure/documentation/-/blob/master/Create_VPC.md?ref_type=heads";

type OnboardStepStatus = "success" | "error" | "skipped";

interface OnboardStep {
  key: string;
  label: string;
  status: OnboardStepStatus;
  message?: string;
}

interface OnboardResult {
  ok: boolean;
  reason?: string;
  steps: OnboardStep[];
}

/** Server double-wraps: axios body = { success, data: OnboardResult, status }. */
interface OnboardResponseBody {
  success: boolean;
  status: number;
  data: OnboardResult;
}

const STEP_ICON: Record<
  OnboardStepStatus,
  { type: string; color: "success" | "danger" | "subdued" }
> = {
  success: { type: "checkInCircleFilled", color: "success" },
  error: { type: "crossInCircleFilled", color: "danger" },
  skipped: { type: "dot", color: "subdued" },
};

/**
 * Shown after a successful save of an opsanio questionnaire. Lets the operator decide
 * whether to create/update the infrastructure project (100-vpc), then triggers the
 * backend onboarding endpoint and renders the per-step result of the bootstrapping
 * process.
 */
export const OnboardProjectModal = ({
  catalogId,
  questionnaireId,
  vpcName,
  onClose,
}: OnboardProjectModalProps): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { Post } = useApi();
  const { keycloak } = useAnketaAuth();
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [result, setResult] = useState<OnboardResult | null>(null);

  useMemo(() => {
    console.log("DEBUG", "VPCNAME", vpcName);
  }, [vpcName]);
  
  const triggerOnboarding = async () => {
    setSubmitting(true);
    setResult(null);

    const response = await Post<OnboardResponseBody>({
      path: `v2/${AnketaResourcesEnum.questionnaires}/${catalogId}/${questionnaireId}/onboard`,
      responseType: "json",
      token: keycloak?.token,
      value: {},
    });
    setSubmitting(false);

    const data = response.data?.data;
    if (response.success && data) {
      setResult(data);
      addToast(
        data.ok
          ? {
              color: "success",
              iconType: "checkInCircleFilled",
              title: translate("anketa.questionnaire.onboard.success.header", {
                defaultMessage: "Infrastructure project triggered",
              }),
              text: translate("anketa.questionnaire.onboard.success.body", {
                defaultMessage:
                  "The 100-vpc project is being created/updated in GitLab.",
              }),
            }
          : {
              color: "warning",
              iconType: "alert",
              title: translate("anketa.questionnaire.onboard.partial.header", {
                defaultMessage: "Onboarding finished with issues",
              }),
              text: translate("anketa.questionnaire.onboard.partial.body", {
                defaultMessage:
                  "Some steps did not complete. See the details below.",
              }),
            },
      );
      return;
    }

    addToast({
      color: "danger",
      iconType: "crossInCircleFilled",
      title: translate("anketa.questionnaire.onboard.error.header", {
        defaultMessage: "Could not start onboarding",
      }),
      text: response.error?.message ?? `HTTP ${response.status}`,
    });
  };

  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          <h1>
            {translate("anketa.questionnaire.onboard.title", {
              defaultMessage: "Create infrastructure project?",
            })}
          </h1>
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        {result ? (
          <>
            <EuiText size="s">
              <h3>
                {translate("anketa.questionnaire.onboard.steps.title", {
                  defaultMessage: "Bootstrapping steps",
                })}
              </h3>
            </EuiText>
            <EuiSpacer size="s" />
            {result.steps.map((s) => (
              <div
                key={s.key}
                style={{ display: "flex", gap: 8, marginBottom: 6 }}
              >
                <EuiIcon
                  type={STEP_ICON[s.status].type}
                  color={STEP_ICON[s.status].color}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <EuiText size="s">
                  <strong>{s.label}</strong>
                  {s.message ? (
                    <EuiText size="xs" color="subdued">
                      {s.message}
                    </EuiText>
                  ) : null}
                </EuiText>
              </div>
            ))}
          </>
        ) : (
          <>
            <EuiText size="s">
              <p>
                {vpcName
                  ? translate("anketa.questionnaire.onboard.body", {
                      values: { vpcName },
                      defaultMessage: `All required values are set. Create or update the 100-vpc GitLab project for "${vpcName}"?`,
                    })
                  : translate("anketa.questionnaire.onboard.body.generic", {
                      defaultMessage:
                        "All required values are set. Create or update the 100-vpc GitLab project?",
                    })}
              </p>
            </EuiText>
            <EuiSpacer size="m" />
            <EuiForm component="form">
              {vpcName ? (
                <EuiFormRow
                  label={translate(
                    "anketa.questionnaire.onboard.vpcName.label",
                    {
                      defaultMessage: "VPC name",
                    },
                  )}
                >
                  <EuiFieldText
                    readOnly
                    value={vpcName}
                    append={
                      <EuiCopy textToCopy={vpcName}>
                        {(copy) => (
                          <EuiButtonEmpty
                            iconType="copyClipboard"
                            size="xs"
                            onClick={copy}
                            aria-label={translate(
                              "anketa.questionnaire.onboard.vpcName.copy",
                              { defaultMessage: "Copy VPC name" },
                            )}
                          />
                        )}
                      </EuiCopy>
                    }
                  />
                </EuiFormRow>
              ) : null}
              <EuiFormRow
                label={translate("anketa.questionnaire.onboard.docs.label", {
                  defaultMessage: "Documentation",
                })}
              >
                <EuiText size="s">
                  <EuiLink
                    href={DOCUMENTATION_VPC_GUIDE_URL}
                    target="_blank"
                    external
                  >
                    {translate("anketa.questionnaire.onboard.docs.link", {
                      defaultMessage: "Create VPC guide",
                    })}
                  </EuiLink>
                </EuiText>
              </EuiFormRow>
            </EuiForm>
          </>
        )}
      </EuiModalBody>

      <EuiModalFooter>
        {result ? (
          <EuiButton fill color="primary" onClick={onClose}>
            {translate("anketa.questionnaire.onboard.close", {
              defaultMessage: "Close",
            })}
          </EuiButton>
        ) : (
          <>
            <EuiButtonEmpty onClick={onClose} disabled={submitting}>
              {translate("anketa.questionnaire.onboard.skip", {
                defaultMessage: "Skip",
              })}
            </EuiButtonEmpty>
            <EuiButton
              fill
              color="primary"
              isLoading={submitting}
              onClick={triggerOnboarding}
            >
              {translate("anketa.questionnaire.onboard.create", {
                defaultMessage: "Create project",
              })}
            </EuiButton>
          </>
        )}
      </EuiModalFooter>
    </EuiModal>
  );
};
